'use client'

import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'

import { AUTH_ROUTES } from '../constants/routes'
import { REVOKE_USER_TOKEN } from '../data/mutations/auth'
import { MY_WORKSPACES } from '../data/queries/workspace'
import { useAuthStore, useSelectedWorkspaceStore } from '../stores/auth-store'
import type { UsersWorkspace } from '../types/user'
import { reset as resetAnalytics } from '../utils/analytics-service'
import { tokenUtils } from '../utils/token-utils'

export const useAuth = () => {
  const store = useAuthStore()
  const selectedWorkspaceStore = useSelectedWorkspaceStore()
  const client = useApolloClient()
  const router = useRouter()

  const handleSetWorkspace = async (workspace: UsersWorkspace | null): Promise<void> => {
    selectedWorkspaceStore.setSelectedWorkspace(workspace)
  }

  const {
    data,
    refetch: refetchWorkspaces,
    loading: isWorkspacesLoading
  } = useQuery<{ myWorkspaces: UsersWorkspace[] }>(MY_WORKSPACES, {
    onCompleted: (res) => {
      const workspaces = res.myWorkspaces
      if (workspaces && workspaces.length > 0) {
        const selectedWorkspaceData = selectedWorkspaceStore.selectedWorkspace
        const activeWorkspace =
          selectedWorkspaceData &&
          workspaces.find(
            (userWorkspace: UsersWorkspace) => userWorkspace?.workspace?.id === selectedWorkspaceData?.workspace?.id
          )

        // If we found the active workspace, check if it has changed (e.g., accessType)
        // Only check on initial load/refresh, not on every query completion
        if (activeWorkspace && selectedWorkspaceData) {
          // Compare key properties that might change (accessType, role, status, etc.)
          const hasChanged =
            activeWorkspace.accessType !== selectedWorkspaceData.accessType ||
            activeWorkspace.role !== selectedWorkspaceData.role ||
            activeWorkspace.status !== selectedWorkspaceData.status ||
            activeWorkspace.workspace.displayName !== selectedWorkspaceData.workspace.displayName ||
            activeWorkspace.workspace.logo !== selectedWorkspaceData.workspace.logo ||
            JSON.stringify(activeWorkspace.workspace.shipmentsPageSettings) !==
              JSON.stringify(selectedWorkspaceData.workspace.shipmentsPageSettings)

          // Update if workspace properties have changed
          if (hasChanged) {
            handleSetWorkspace(activeWorkspace)
          }
        } else if (activeWorkspace && !selectedWorkspaceData) {
          // Only update if we found the active workspace, and it's different from current
          handleSetWorkspace(activeWorkspace)
        }
      }
    }
  })

  const [logoutMutation, { loading: isLoggingOut }] = useMutation(REVOKE_USER_TOKEN, {
    onError: (error) => {
      console.error('Logout error:', error)
      toast.error('Error logging out')
    }
  })

  const checkWorkspaceValidity = async (workspacename: string): Promise<boolean> => {
    try {
      const cachedWorkspaces = client.readQuery({ query: MY_WORKSPACES })
      const workspaces = cachedWorkspaces || (await refetchWorkspaces()).data

      const validWorkspace = workspaces?.myWorkspaces?.some(
        (ws: UsersWorkspace) => ws.workspace?.workspacename === workspacename
      )

      if (validWorkspace) {
        const workspace = workspaces.myWorkspaces.find(
          (ws: UsersWorkspace) => ws.workspace?.workspacename === workspacename
        )
        selectedWorkspaceStore.setSelectedWorkspace(workspace)
      }

      return Boolean(validWorkspace)
    } catch (error) {
      console.error('Workspace validation error:', error)
      return false
    }
  }

  const handleLogout = async (onSuccess?: () => void) => {
    try {
      toast.info('Logging out...')
      await logoutMutation({
        variables: { refreshToken: tokenUtils.getToken('Refresh-Token') || '' }
      }).then(() => {
        onSuccess?.()
        client.clearStore()
        store.clearAuth()
        tokenUtils.clearLocalStore()
        resetAnalytics()
        router.replace(AUTH_ROUTES.LOGIN)
        toast.success('Logged out successfully')
      })
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Error logging out')
    }
  }

  return {
    ...store,
    doesUserBelongToWorkspace: checkWorkspaceValidity,
    handleLogout,
    isLoggingOut,
    isUserAdminOrOwner: store.isUserAdminOrOwner,
    isWorkspacesLoading,
    onValidWorkspace: checkWorkspaceValidity,
    refetchWorkspaces: refetchWorkspaces,
    selectedWorkspace: selectedWorkspaceStore.selectedWorkspace,
    setSelectedWorkspace: selectedWorkspaceStore.setSelectedWorkspace,
    setWorkspace: handleSetWorkspace,
    workspaces: data?.myWorkspaces
  }
}
